/**
 * useInvestorMetricDetail — fetch the per-metric drill-in (Stage 2c).
 *
 * GET /api/v1/investor/metric/:metricId?from=&to=
 *   Returns { trend, mix, contributors } — each section optional.
 *
 * Per CEO directive 2026-04-28 (no mock anywhere): on failure data stays
 * null and the modal renders the existing "Stage 2c coming" placeholder.
 *
 * Wallet metrics are intentionally excluded from the route — current
 * snapshots have no time dimension. The hook short-circuits with `data:null`
 * for them so we don't waste a roundtrip.
 */
import { useEffect, useState } from "react";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4000/api/v1";
const FETCH_TIMEOUT_MS = 8000;

const SUPPORTED = new Set([
  "brand-deals-lifetime", "gmv-lifetime", "creator-payouts-lifetime", "refunds-lifetime",
  "creators-brought-fans", "fans-brought-fans", "creators-brought-creators", "viral-coefficient",
  // Home-page tile drill-ins
  "revenue-today", "fans-joined-today", "creators-joined-today",
  // Fans-page tile drill-ins
  "total-fans", "active-fans-window", "paying-fans-window", "vip-fans-window",
]);

function withTimeout(promise, ms, controller) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      controller.abort();
      reject(new Error(`timeout after ${ms}ms`));
    }, ms);
    promise.then(
      (v) => { clearTimeout(timer); resolve(v); },
      (e) => { clearTimeout(timer); reject(e); },
    );
  });
}

export function useInvestorMetricDetail(metricId, range, rangeId = null) {
  const [data, setData]       = useState(null);
  const [error, setError]     = useState(null);
  const [loading, setLoading] = useState(false);

  const rangeKey = range ? `${range.from || ''}|${range.to || ''}|${rangeId || ''}` : '';

  useEffect(() => {
    if (!metricId || !SUPPORTED.has(metricId)) {
      setData(null); setError(null); setLoading(false);
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    setLoading(true); setError(null); setData(null);

    (async () => {
      try {
        const token  = (typeof localStorage !== "undefined") ? localStorage.getItem("fc_token") : null;
        const apiKey = process.env.REACT_APP_API_KEY;
        const headers = {};
        if (token)       headers.Authorization = `Bearer ${token}`;
        else if (apiKey) headers["X-API-Key"]  = apiKey;
        const params = new URLSearchParams();
        if (range && range.from) params.set('from', range.from);
        if (range && range.to)   params.set('to',   range.to);
        if (rangeId) params.set('rangeId', rangeId);
        const qs = params.toString();
        const url = `${API_BASE}/investor/metric/${encodeURIComponent(metricId)}${qs ? `?${qs}` : ''}`;
        const res = await withTimeout(
          fetch(url, {
            headers,
            signal: controller.signal,
          }),
          FETCH_TIMEOUT_MS, controller,
        );
        if (cancelled) return;
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!json || !json.success || !json.data) throw new Error("malformed response envelope");
        setData(json.data);
      } catch (e) {
        if (!cancelled) { setError(e.message || String(e)); setData(null); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metricId, rangeKey]);

  return { data, loading, error };
}
